import React, { useEffect, useState } from "react";
import { Button } from "@fluentui/react-components";
import { observer } from "mobx-react-lite";
import { browserName, isDesktop } from "react-device-detect";
import { constants } from "../../shared/constants";
import Loader from "../../shared/loader/Loader";
import { useStore } from "../../stores/Stores";
import styles from "./landingPageContainer.module.scss";
import { MeetingStatus } from "../../shared/types/MeetingStatus";
import { closeWindow } from "../../shared/utils/common";
import { BusinessLine } from "../../shared/types/BusinessLine";
import { CancelMeetingPageState } from "./requestCancelMeeting/CancelMeetingPageState";
import { useTranslation } from "../../shared/translation/Translation";

export enum LandingPageType {
    Join = "joinMeeting",
    RequestCancel = "requestCancelMeeting",
}

interface MeetingContainerProps {
    section: LandingPageType;

    cancelMeetingPageState?: CancelMeetingPageState;

    primaryAction(urlHash: string | null): void;
}

function getUrlHash() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(constants.joinUrlHashQueryParameterName);
}

const LandingPageContainer: React.FC<MeetingContainerProps> = observer((props: MeetingContainerProps) => {
    const translations = useTranslation();
    const messages: any = translations[props.section];
    const [urlHash] = useState<string | null>(getUrlHash());

    const { meetingStore } = useStore();

    const isBrowserSupported = !isDesktop || (isDesktop && constants.recommendedBrowsers.includes(browserName));

    useEffect(() => {
        (async function () {
            if (urlHash === null) {
                return;
            }

            await meetingStore.loadMeeting(urlHash);
        })();
    }, [meetingStore, urlHash]);

    function renderLogo() {
        switch (meetingStore.meeting.businessLine) {
            case BusinessLine.Corporate:
                return (
                    <img src="/assets/mbank_corpo.png" alt={translations.logo.corporation} className={styles.image} />
                );
            case BusinessLine.Retail:
                return <img src="/assets/mbank_detal.png" alt={translations.logo.retail} className={styles.image} />;
            case BusinessLine.Private:
                return <img src="/assets/mbank_private.png" alt={translations.logo.private} className={styles.image} />;
        }
    }

    function renderDescription() {
        if (
            urlHash === null ||
            (meetingStore.loadMeetingRequestStatus.isDone && meetingStore.meeting.status === undefined)
        ) {
            return translations.joinMeeting.meetingNotFound;
        }

        if (meetingStore.meeting.isCancelled) {
            return translations.common.meetingCancelled;
        }

        if (props.cancelMeetingPageState === CancelMeetingPageState.RequestSent) {
            return translations.requestCancelMeeting.requestSent;
        }

        if (!isBrowserSupported && props.section === LandingPageType.Join) {
            return translations.joinMeeting.browserUnsupportedError(constants.recommendedBrowsersFullNames.join(", "));
        }

        switch (meetingStore.meeting.status) {
            case MeetingStatus.Pending:
                return messages.pendingDescription(meetingStore.meeting.startAt);
            case MeetingStatus.InProgress:
                return props.section === LandingPageType.Join
                    ? messages.inProgressDescription
                    : messages.inProgressDescription(meetingStore.meeting.startAt);
            case MeetingStatus.Expired:
                return messages.expiredDescription(meetingStore.meeting.endAt);
        }
    }

    const canShowActionButtons =
        (props.section === LandingPageType.RequestCancel &&
            meetingStore.meeting.status === MeetingStatus.Pending &&
            !meetingStore.meeting.isCancelled &&
            props.cancelMeetingPageState !== CancelMeetingPageState.RequestSent) ||
        (props.section === LandingPageType.Join &&
            meetingStore.meeting.status === MeetingStatus.InProgress &&
            !meetingStore.meeting.isCancelled &&
            isBrowserSupported);

    return (
        <>
            {meetingStore.getOriginJoinUrlRequestStatus.isPending ||
            meetingStore.loadMeetingRequestStatus.isPending ||
            meetingStore.requestCancelMeetingRequestStatus.isPending ? (
                <Loader text={translations.common.loader} additionalClassName={styles.loader} />
            ) : (
                <div className={styles.wrapper}>
                    <div className={styles.logoWrapper}>{renderLogo()}</div>
                    <div className={styles.content}>{renderDescription()}</div>
                    {canShowActionButtons && (
                        <div className={styles.buttonsWrapper}>
                            <Button className={styles.button} onClick={closeWindow}>
                                {messages.redirectButton}
                            </Button>
                            <Button appearance="primary" onClick={() => props.primaryAction(urlHash)}>
                                {messages.actionButton}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
});

export default LandingPageContainer;
